(function () {
  'use strict';

  const SELECTORS = {
    mobileMenuToggle: '#MobileMenuToggle, .cikada-mobile-menu-toggle',
    mobileMenu: '#MobileMenu, .cikada-mobile-menu',

    cartTrigger: '#CartDrawerTrigger, .cikada-cart-trigger',
    cartOverlay: '#CartDrawerOverlay, .cikada-cart-overlay',
    cartDrawer: '#CartDrawerContent, .cikada-cart-drawer',
    cartClose: '#CartDrawerClose, .cikada-cart-close',

    cartBadge: '#CartBadgeCount, .cikada-cart-badge',
    cartItems: '#CartDrawerItems, .cikada-cart-items',
    cartSubtotal: '#CartSubtotalDisplay, .cikada-cart-subtotal',

    productForm: 'form[action*="/cart/add"]',
    variantInput: 'select[name="id"], input[name="id"], [data-variant-id]',
    quantityInput: 'input[name="quantity"], .cikada-quantity-input',

    cartQuantityPlus: '.cikada-cart-qty-plus',
    cartQuantityMinus: '.cikada-cart-qty-minus',
    cartRemove: '.cikada-cart-remove'
  };

  let bodyScrollLocked = false;

  /* ============================================================
     UTILITIES
     ============================================================ */

  function lockBodyScroll() {
    if (!bodyScrollLocked) {
      document.body.dataset.cikadaScrollLock = 'true';
      document.body.style.overflow = 'hidden';
      bodyScrollLocked = true;
    }
  }

  function unlockBodyScroll() {
    if (bodyScrollLocked) {
      document.body.style.overflow = '';
      delete document.body.dataset.cikadaScrollLock;
      bodyScrollLocked = false;
    }
  }

  function getElement(selector) {
    return document.querySelector(selector);
  }

  function getElements(selector) {
    return document.querySelectorAll(selector);
  }

  function setLoading(button, loading, loadingText) {
    if (!button) return;

    if (loading) {
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent.trim();
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      if (loadingText) {
        button.textContent = loadingText;
      }
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');

      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
      }
    }
  }

  async function fetchJSON(url, options) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const message =
        data && data.description
          ? data.description
          : 'Something went wrong. Please try again.';

      throw new Error(message);
    }

    return data;
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function'
    ) {
      const format =
        window.Shopify.money_format ||
        window.theme?.moneyFormat ||
        '₹{{amount}}';

      return window.Shopify.formatMoney(cents, format);
    }

    return '₹' + amount.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  /* ============================================================
     MOBILE MENU
     ============================================================ */

  function initMobileMenu() {
    const toggle = getElement(SELECTORS.mobileMenuToggle);
    const menu = getElement(SELECTORS.mobileMenu);

    if (!toggle || !menu) return;

    function openMenu() {
      menu.classList.add('is-active', 'open');
      menu.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      lockBodyScroll();
    }

    function closeMenu() {
      menu.classList.remove('is-active', 'open');
      menu.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');

      if (!getElement(SELECTORS.cartOverlay)?.classList.contains('is-active')) {
        unlockBodyScroll();
      }
    }

    toggle.addEventListener('click', function (event) {
      event.preventDefault();

      const isOpen =
        menu.classList.contains('is-active') ||
        menu.classList.contains('open');

      isOpen ? closeMenu() : openMenu();
    });

    menu.addEventListener('click', function (event) {
      const link = event.target.closest('a');

      if (link) {
        closeMenu();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeMenu();
      }
    });

    toggle.setAttribute(
      'aria-expanded',
      menu.classList.contains('is-active') ? 'true' : 'false'
    );

    menu.setAttribute(
      'aria-hidden',
      menu.classList.contains('is-active') ? 'false' : 'true'
    );
  }

  /* ============================================================
     CART DRAWER
     ============================================================ */

  function initCartDrawer() {
    const trigger = getElement(SELECTORS.cartTrigger);
    const overlay = getElement(SELECTORS.cartOverlay);
    const drawer = getElement(SELECTORS.cartDrawer);

    if (!overlay || !drawer) return;

    function openCart() {
      overlay.classList.add('is-active');
      drawer.classList.add('is-active');

      overlay.setAttribute('aria-hidden', 'false');
      lockBodyScroll();

      refreshCart();
    }

    function closeCart() {
      overlay.classList.remove('is-active');
      drawer.classList.remove('is-active');

      overlay.setAttribute('aria-hidden', 'true');

      const menu = getElement(SELECTORS.mobileMenu);

      if (!menu || !menu.classList.contains('is-active')) {
        unlockBodyScroll();
      }
    }

    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        openCart();
      });
    }

    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) {
        closeCart();
      }
    });

    document.addEventListener('click', function (event) {
      const closeButton = event.target.closest(
        '[data-cart-close], .cikada-cart-close'
      );

      if (closeButton) {
        event.preventDefault();
        closeCart();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeCart();
      }
    });

    window.cikadaOpenCart = openCart;
    window.cikadaCloseCart = closeCart;

    overlay.setAttribute(
      'aria-hidden',
      overlay.classList.contains('is-active') ? 'false' : 'true'
    );
  }

  /* ============================================================
     CART DATA
     ============================================================ */

  async function getCart() {
    return fetchJSON('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });
  }

  async function refreshCart() {
    try {
      const cart = await getCart();
      updateCartInterface(cart);
      return cart;
    } catch (error) {
      console.error('CIKADA cart error:', error);
      return null;
    }
  }

  /* ============================================================
     CART INTERFACE
     ============================================================ */

  function updateCartInterface(cart) {
    if (!cart) return;

    updateCartBadge(cart);
    updateCartSubtotal(cart);
    updateCartItems(cart);
  }

  function updateCartBadge(cart) {
    const badges = getElements(SELECTORS.cartBadge);

    badges.forEach(function (badge) {
      badge.textContent = cart.item_count;

      if (cart.item_count > 0) {
        badge.removeAttribute('hidden');
        badge.classList.remove('hidden');
      } else {
        badge.setAttribute('hidden', 'hidden');
        badge.classList.add('hidden');
      }
    });
  }

  function updateCartSubtotal(cart) {
    const subtotal = getElement(SELECTORS.cartSubtotal);

    if (!subtotal) return;

    subtotal.textContent = formatMoney(cart.total_price);
  }

  function escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function updateCartItems(cart) {
    const container = getElement(SELECTORS.cartItems);

    if (!container) return;

    if (!cart.items || cart.items.length === 0) {
      container.innerHTML = `
        <div class="cikada-cart-empty">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="cikada-btn cikada-btn-outline">
            Explore Collection
          </a>
        </div>
      `;

      return;
    }

    const fragment = document.createDocumentFragment();

    cart.items.forEach(function (item) {
      const article = document.createElement('article');

      article.className = 'cikada-cart-item';
      article.dataset.key = item.key;

      const image = item.featured_image?.url || item.image || '';

      const variantTitle =
        item.variant_title &&
        item.variant_title !== 'Default Title'
          ? item.variant_title
          : '';

      article.innerHTML = `
        <div class="cikada-cart-item__image">
          ${
            image
              ? `<img
                  src="${escapeHTML(image)}"
                  alt="${escapeHTML(item.product_title)}"
                  loading="lazy"
                  width="160"
                  height="200"
                >`
              : ''
          }
        </div>

        <div class="cikada-cart-item__content">

          <div class="cikada-cart-item__top">
            <div>
              <h4 class="cikada-cart-item__title">
                ${escapeHTML(item.product_title)}
              </h4>

              ${
                variantTitle
                  ? `<p class="cikada-cart-item__variant">
                      ${escapeHTML(variantTitle)}
                    </p>`
                  : ''
              }
            </div>

            <button
              type="button"
              class="cikada-cart-remove"
              data-key="${escapeHTML(item.key)}"
              aria-label="Remove ${escapeHTML(item.product_title)}"
            >
              ×
            </button>
          </div>

          <div class="cikada-cart-item__bottom">

            <div class="cikada-cart-quantity">
              <button
                type="button"
                class="cikada-cart-qty-minus"
                data-key="${escapeHTML(item.key)}"
                data-quantity="${Math.max(0, item.quantity - 1)}"
                aria-label="Decrease quantity"
              >
                −
              </button>

              <span class="cikada-cart-quantity__value">
                ${item.quantity}
              </span>

              <button
                type="button"
                class="cikada-cart-qty-plus"
                data-key="${escapeHTML(item.key)}"
                data-quantity="${item.quantity + 1}"
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>

            <span class="cikada-cart-item__price">
              ${formatMoney(item.final_line_price)}
            </span>

          </div>
        </div>
      `;

      fragment.appendChild(article);
    });

    container.replaceChildren(fragment);
  }

  /* ============================================================
     ADD TO CART
     ============================================================ */

  function initAddToCart() {
    document.addEventListener('submit', async function (event) {
      const form = event.target;

      if (!form.matches(SELECTORS.productForm)) {
        return;
      }

      event.preventDefault();

      const submitButton = form.querySelector(
        '[type="submit"], button[name="add"]'
      );

      const formData = new FormData(form);

      if (!formData.get('id')) {
        showCartError('Please select an available variant.');
        return;
      }

      try {
        setLoading(submitButton, true, 'ADDING...');

        await fetchJSON('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: formData
        });

        setLoading(submitButton, false);

        if (submitButton) {
          const originalText =
            submitButton.dataset.originalText || 'ADD TO CART';

          submitButton.textContent = 'ADDED';

          window.setTimeout(function () {
            submitButton.textContent = originalText;
          }, 1500);
        }

        const cart = await refreshCart();

        if (
          cart &&
          typeof window.cikadaOpenCart === 'function'
        ) {
          window.cikadaOpenCart();
        }
      } catch (error) {
        console.error('CIKADA add-to-cart error:', error);

        setLoading(submitButton, false);

        if (submitButton) {
          submitButton.textContent = 'TRY AGAIN';

          window.setTimeout(function () {
            submitButton.textContent =
              submitButton.dataset.originalText || 'ADD TO CART';
          }, 2000);
        }

        showCartError(error.message);
      }
    });
  }

  function showCartError(message) {
    const drawer = getElement(SELECTORS.cartDrawer);

    if (!drawer) {
      return;
    }

    let errorElement = drawer.querySelector('.cikada-cart-error');

    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'cikada-cart-error';
      errorElement.setAttribute('role', 'alert');

      drawer.prepend(errorElement);
    }

    errorElement.textContent =
      message || 'Unable to update your cart. Please try again.';

    window.setTimeout(function () {
      if (errorElement) {
        errorElement.remove();
      }
    }, 4000);
  }

  /* ============================================================
     CART QUANTITY / REMOVE
     ============================================================ */

  async function changeCartItem(key, quantity, button) {
    if (!key) return;

    const safeQuantity = Math.max(
      0,
      Number.isFinite(Number(quantity)) ? Number(quantity) : 0
    );

    try {
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }

      const cart = await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: safeQuantity
        })
      });

      updateCartInterface(cart);
    } catch (error) {
      console.error('CIKADA cart update error:', error);
      showCartError(error.message);
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  function initCartControls() {
    document.addEventListener('click', function (event) {
      const plus = event.target.closest(
        SELECTORS.cartQuantityPlus
      );

      const minus = event.target.closest(
        SELECTORS.cartQuantityMinus
      );

      const remove = event.target.closest(
        SELECTORS.cartRemove
      );

      if (plus) {
        event.preventDefault();

        const key = plus.dataset.key;
        const quantity = Number(plus.dataset.quantity);

        changeCartItem(key, quantity, plus);
        return;
      }

      if (minus) {
        event.preventDefault();

        const key = minus.dataset.key;
        const quantity = Number(minus.dataset.quantity);

        changeCartItem(key, quantity, minus);
        return;
      }

      if (remove) {
        event.preventDefault();

        const key = remove.dataset.key;

        changeCartItem(key, 0, remove);
      }
    });
  }

  /* ============================================================
     PRODUCT VARIANTS
     ============================================================ */

  function initProductVariants() {
    const variantInputs = getElements(
      SELECTORS.variantInput
    );

    if (!variantInputs.length) return;

    variantInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        const variantId =
          input.value ||
          input.dataset.variantId;

        if (!variantId) return;

        const url = new URL(window.location.href);

        url.searchParams.set('variant', variantId);

        window.history.replaceState(
          {},
          '',
          url.toString()
        );

        updateVariantForms(variantId);
      });
    });
  }

  function updateVariantForms(variantId) {
    const idInputs = document.querySelectorAll(
      'form[action*="/cart/add"] input[name="id"],' +
      'form[action*="/cart/add"] select[name="id"]'
    );

    idInputs.forEach(function (input) {
      if (
        input.tagName === 'SELECT'
      ) {
        input.value = variantId;
      } else {
        input.value = variantId;
      }
    });
  }

  /* ============================================================
     INITIALIZATION
     ============================================================ */

  function init() {
    initMobileMenu();
    initCartDrawer();
    initAddToCart();
    initCartControls();
    initProductVariants();

    refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }

})();